import React, { useEffect, useMemo, useState } from 'react';
import TextField from 'components/Form/TextField';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from 'components/Form/Button';
import Spacer from 'components/Spacer';
import { Icon, Icons } from 'components/Icon';
import { BeneficiaryTypeEnum } from '../types/user.enum.type';
import { TrusteesType } from '../types/on-boarding.type';
import {
  useCreateTrustee,
  useDeleteTrustee,
  useGetTrusteeList
} from 'common/queries-and-mutations/trustee';
import { Loader } from 'components/Loader';
import { findNextPage, findPreviousPage, showRemoveButton } from 'utils';
import TextAreaFieldWithAutoHeightResize from 'components/Form/TextAreaFieldWithAutoHeightResize';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectAccountDetails } from 'selectors/account-selector';
import { fetchUserInformation } from 'thunks/account-thunk';
import { STEPS } from 'constants/steps';
import InfoBar from './InfoBar';

export default function TrusteeInfo() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLater, setIsLater] = useState(false);

  const { mutate, isSuccess, isLoading, isError } = useCreateTrustee();
  const { mutate: deleteTrustee, isLoading: isDeleting } = useDeleteTrustee();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(selectAccountDetails);
  const planAllowPage = user && JSON.parse(user?.planAllowPage);

  const parts = window.location.pathname.split('/');
  const parentPath = parts[1];

  const {
    data,
    isSuccess: isTrusteeSuccess,
    isLoading: isTrusteeLoading
  } = useGetTrusteeList();

  const trustees = useMemo(() => {
    if (isTrusteeSuccess && data.length > 0) {
      return data.map(trustee => ({
        trusteeId: trustee.id,
        trusteeFirstName: trustee.trusteeFirstName,
        trusteeMiddleName: trustee.trusteeMiddleName,
        trusteeLastName: trustee.trusteeLastName,
        trusteeType: trustee.trusteeType,
        address: trustee.address,
        emailAddress: trustee.emailAddress,
        phoneNumber: trustee.phoneNumber,
        dutiesInstruction: trustee.dutiesInstruction,
        rcNumber: trustee.rcNumber,
        incorporationDate: trustee.incorporationDate,
        incorporationPlace: trustee.incorporationPlace
      }));
    } else {
      return [
        {
          trusteeId: null,
          trusteeFirstName: '',
          trusteeMiddleName: '',
          trusteeLastName: '',
          trusteeType: '',
          address: '',
          emailAddress: '',
          phoneNumber: '',
          dutiesInstruction: '',
          rcNumber: '',
          incorporationDate: '',
          incorporationPlace: ''
        }
      ];
    }
  }, [data, isTrusteeSuccess]);

  const formik = useFormik({
    initialValues: {
      trustees
    },
    validationSchema: Yup.object({
      trustees: Yup.array().of(
        Yup.object().shape({
          trusteeFirstName: Yup.string().when('trusteeType', {
            is: BeneficiaryTypeEnum.INDIVIDUAL,
            then: Yup.string()
              .required('Trustee first name is required')
              .nullable(),
            otherwise: Yup.string().required('Name is required').nullable()
          }),
          trusteeLastName: Yup.string().when('trusteeType', {
            is: BeneficiaryTypeEnum.INDIVIDUAL,
            then: Yup.string()
              .required('Trustee last name is required')
              .nullable(),
            otherwise: Yup.string()
          }),
          trusteeType: Yup.string()
            .required('Trustee type is required')
            .nullable(),
          address: Yup.string().required('Address is required').nullable(),
          emailAddress: Yup.string()
            .email('Invalid email address')
            .required('Email is required')
            .nullable(),
          phoneNumber: Yup.string().when('trusteeType', {
            is: BeneficiaryTypeEnum.INDIVIDUAL,
            then: Yup.string().required('Phone number is required').nullable(),
            otherwise: Yup.string()
          }),
          dutiesInstruction: Yup.string().when('trusteeType', {
            is: BeneficiaryTypeEnum.CORPORATE,
            then: Yup.string()
              .required('Duties and Instructions is required')
              .nullable(),
            otherwise: Yup.string()
          }),
          rcNumber: Yup.string().when('trusteeType', {
            is: BeneficiaryTypeEnum.CORPORATE,
            then: Yup.string().required('RC Number is required').nullable(),
            otherwise: Yup.string()
          })
        })
      )
    }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onTrusteeSubmit(values);
    }
  });

  const addMoreTrustee = () => {
    formik.values.trustees.push({
      trusteeId: null,
      trusteeFirstName: '',
      trusteeMiddleName: '',
      trusteeLastName: '',
      trusteeType: '',
      address: '',
      emailAddress: '',
      phoneNumber: '',
      dutiesInstruction: '',
      rcNumber: '',
      incorporationDate: '',
      incorporationPlace: ''
    });
    formik.setFieldValue('trustees', [...formik.values.trustees]);
  };

  const removeTrustee = (indexToRemove: number, trusteeId?: number | null) => {
    if (trusteeId) {
      deleteTrustee(trusteeId);
    }
    formik.values.trustees.splice(indexToRemove, 1);
    formik.setFieldValue('trustees', [...formik.values.trustees]);
  };

  const onTrusteeSubmit = (formValue: TrusteesType) => {
    mutate(formValue);
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      setIsSubmitting(false);
      dispatch(fetchUserInformation());
      if (isLater) {
        navigate('/on-boarding/welcome');
        setIsLater(false);
        return;
      }
      navigate(findNextPage(planAllowPage, STEPS.TRUSTEE_INFORMATION));
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError, dispatch, navigate, planAllowPage]);

  const onContinueLater = () => {
    if (Object.keys(formik.errors).length) {
      return;
    }
    mutate(formik.values);
  };

  const onPrevious = () => {
    navigate(findPreviousPage(planAllowPage, STEPS.TRUSTEE_INFORMATION));
  };

  return (
    <div className="mx-10">
      <InfoBar
        stepCounter={1}
        stepPercentage={STEPS.TRUSTEE_INFORMATION}
        timelineStep={3}
        lastPage
      />
      {isTrusteeLoading ? (
        <div className="mt-40 flex flex-col items-center justify-center">
          <Loader color={'#FF6606'} height={50} width={50} />
        </div>
      ) : (
        <>
          <p className="my-9 text-center font-circular text-lg font-medium text-[#353535]">
            Trustee Information
          </p>
          <form
            autoComplete="false"
            className="m-10"
            onSubmit={formik.handleSubmit}
          >
            {formik.values.trustees.map((trustee, index) => (
              <div key={index} className="mb-1 mt-8">
                <div className="grid md:grid-cols-2 md:gap-10">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`trustees[${index}].trusteeFirstName`}
                      placeholder=""
                      value={trustee.trusteeFirstName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label={
                        trustee.trusteeType === BeneficiaryTypeEnum.INDIVIDUAL
                          ? 'Trustee First Name'
                          : 'Name'
                      }
                      error={
                        formik.touched.trustees
                          ? formik.errors.trustees?.[index] &&
                            formik.errors.trustees.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.trustees[index])
                              ).trusteeFirstName
                            : ''
                          : ''
                      }
                      isRequired
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <div className="relative mb-2 flex items-center justify-between">
                      <span className="flex items-center">
                        <label className="text-[12px] font-medium text-[#353535]">
                          Type of Trustee
                        </label>
                        <div className="mx-2 mb-2">
                          <Spacer width={3} />
                          <Icon name={Icons.Required} />
                        </div>
                      </span>
                    </div>
                    <div className="mt-5 flex items-center gap-10">
                      <button
                        type="button"
                        onClick={() =>
                          formik.setFieldValue(
                            `trustees[${index}].trusteeType`,
                            BeneficiaryTypeEnum.INDIVIDUAL
                          )
                        }
                        className="flex items-center gap-3"
                      >
                        <span className="text-base font-bold text-[#353535]">
                          Individual
                        </span>
                        <Icon
                          name={
                            trustee.trusteeType ===
                            BeneficiaryTypeEnum.INDIVIDUAL
                              ? Icons.CheckedBox
                              : Icons.UncheckBox
                          }
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          formik.setFieldValue(
                            `trustees[${index}].trusteeType`,
                            BeneficiaryTypeEnum.CORPORATE
                          )
                        }
                        className="flex items-center gap-3"
                      >
                        <span className="text-base font-bold text-[#353535]">
                          Corporate
                        </span>
                        <Icon
                          name={
                            trustee.trusteeType ===
                            BeneficiaryTypeEnum.CORPORATE
                              ? Icons.CheckedBox
                              : Icons.UncheckBox
                          }
                        />
                      </button>
                    </div>
                  </div>
                </div>
                {trustee.trusteeType === BeneficiaryTypeEnum.INDIVIDUAL && (
                  <div className="grid md:grid-cols-2 md:gap-10">
                    <div className="group relative z-0 mb-6 w-full">
                      <TextField
                        name={`trustees[${index}].trusteeMiddleName`}
                        placeholder=""
                        value={trustee.trusteeMiddleName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Trustee Middle Name"
                        error={
                          formik.touched.trustees
                            ? formik.errors.trustees?.[index] &&
                              formik.errors.trustees.length > 0
                              ? JSON.parse(
                                  JSON.stringify(formik.errors.trustees[index])
                                ).trusteeMiddleName
                              : ''
                            : ''
                        }
                        isRequired
                        size="lg"
                        className="rounded-2xl border-[1.5px] border-gray-300"
                      />
                    </div>
                    <div className="group relative z-0 mb-6 w-full">
                      <TextField
                        name={`trustees[${index}].trusteeLastName`}
                        placeholder=""
                        value={trustee.trusteeLastName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Trustee Last Name"
                        error={
                          formik.touched.trustees
                            ? formik.errors.trustees?.[index] &&
                              formik.errors.trustees.length > 0
                              ? JSON.parse(
                                  JSON.stringify(formik.errors.trustees[index])
                                ).trusteeLastName
                              : ''
                            : ''
                        }
                        isRequired
                        size="lg"
                        className="rounded-2xl border-[1.5px] border-gray-300"
                      />
                    </div>
                  </div>
                )}
                {trustee.trusteeType === BeneficiaryTypeEnum.INDIVIDUAL && (
                  <div>
                    <div className="grid md:grid-cols-2 md:gap-10">
                      <div className="group relative z-0 mb-6 w-full">
                        <TextField
                          name={`trustees[${index}].address`}
                          placeholder=""
                          isRequired
                          value={trustee.address}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          label="Home Address"
                          error={
                            formik.touched.trustees
                              ? formik.errors.trustees?.[index] &&
                                formik.errors.trustees.length > 0
                                ? JSON.parse(
                                    JSON.stringify(
                                      formik.errors.trustees[index]
                                    )
                                  ).address
                                : ''
                              : ''
                          }
                          size="lg"
                          className="rounded-2xl border-[1.5px] border-gray-300"
                        />
                      </div>
                      <div className="group relative z-0 mb-6 w-full">
                        <TextField
                          name={`trustees[${index}].emailAddress`}
                          placeholder=""
                          value={trustee.emailAddress}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          label="Email Address"
                          isRequired
                          error={
                            formik.touched.trustees
                              ? formik.errors.trustees?.[index] &&
                                formik.errors.trustees.length > 0
                                ? JSON.parse(
                                    JSON.stringify(
                                      formik.errors.trustees[index]
                                    )
                                  ).emailAddress
                                : ''
                              : ''
                          }
                          size="lg"
                          className="rounded-2xl border-[1.5px] border-gray-300"
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 md:gap-10">
                      <div className="group relative z-0 mb-6 w-full">
                        <TextField
                          name={`trustees[${index}].phoneNumber`}
                          placeholder=""
                          isRequired
                          value={trustee.phoneNumber}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          label="Phone Number"
                          error={
                            formik.touched.trustees
                              ? formik.errors.trustees?.[index] &&
                                formik.errors.trustees.length > 0
                                ? JSON.parse(
                                    JSON.stringify(
                                      formik.errors.trustees[index]
                                    )
                                  ).phoneNumber
                                : ''
                              : ''
                          }
                          size="lg"
                          className="rounded-2xl border-[1.5px] border-gray-300"
                        />
                      </div>
                      <div className="group relative z-0 mb-6 w-full">
                        <TextAreaFieldWithAutoHeightResize
                          label="Duties and Instructions"
                          isRequired
                          name={`trustees[${index}].dutiesInstruction`}
                          value={trustee.dutiesInstruction}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder=""
                          rows={6}
                          error={
                            formik.touched.trustees
                              ? formik.errors.trustees?.[index] &&
                                formik.errors.trustees.length > 0
                                ? JSON.parse(
                                    JSON.stringify(
                                      formik.errors.trustees[index]
                                    )
                                  ).dutiesInstruction
                                : ''
                              : ''
                          }
                          className="rounded-2xl border-[1.5px] border-gray-300"
                        />
                      </div>
                    </div>
                  </div>
                )}
                {trustee.trusteeType === BeneficiaryTypeEnum.CORPORATE && (
                  <div>
                    <div className="grid md:grid-cols-2 md:gap-10">
                      <div className="group relative z-0 mb-6 w-full">
                        <TextField
                          name={`trustees[${index}].rcNumber`}
                          placeholder=""
                          value={trustee.rcNumber}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          label="RC Number"
                          isRequired
                          error={
                            formik.touched.trustees
                              ? formik.errors.trustees?.[index] &&
                                formik.errors.trustees.length > 0
                                ? JSON.parse(
                                    JSON.stringify(
                                      formik.errors.trustees[index]
                                    )
                                  ).rcNumber
                                : ''
                              : ''
                          }
                          size="lg"
                          className="rounded-2xl border-[1.5px] border-gray-300"
                        />
                      </div>
                      <div className="group relative z-0 mb-6 w-full">
                        <TextField
                          name={`trustees[${index}].address`}
                          placeholder=""
                          isRequired
                          value={trustee.address}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          label="Company Address"
                          error={
                            formik.touched.trustees
                              ? formik.errors.trustees?.[index] &&
                                formik.errors.trustees.length > 0
                                ? JSON.parse(
                                    JSON.stringify(
                                      formik.errors.trustees[index]
                                    )
                                  ).address
                                : ''
                              : ''
                          }
                          size="lg"
                          className="rounded-2xl border-[1.5px] border-gray-300"
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 md:gap-10">
                      <div className="group relative z-0 mb-6 w-full">
                        <TextField
                          name={`trustees[${index}].emailAddress`}
                          placeholder=""
                          value={trustee.emailAddress}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          label="Email Address"
                          isRequired
                          error={
                            formik.touched.trustees
                              ? formik.errors.trustees?.[index] &&
                                formik.errors.trustees.length > 0
                                ? JSON.parse(
                                    JSON.stringify(
                                      formik.errors.trustees[index]
                                    )
                                  ).emailAddress
                                : ''
                              : ''
                          }
                          size="lg"
                          className="rounded-2xl border-[1.5px] border-gray-300"
                        />
                      </div>
                      <div className="group relative z-0 mb-6 w-full">
                        <TextField
                          name={`trustees[${index}].incorporationDate`}
                          placeholder=""
                          value={trustee.incorporationDate}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          label="Date of Incorporation"
                          error={
                            formik.touched.trustees
                              ? formik.errors.trustees?.[index] &&
                                formik.errors.trustees.length > 0
                                ? JSON.parse(
                                    JSON.stringify(
                                      formik.errors.trustees[index]
                                    )
                                  ).incorporationDate
                                : ''
                              : ''
                          }
                          size="lg"
                          className="rounded-2xl border-[1.5px] border-gray-300"
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 md:gap-10">
                      <div className="group relative z-0 mb-6 w-full">
                        <TextField
                          name={`trustees[${index}].incorporationPlace`}
                          placeholder=""
                          value={trustee.incorporationPlace}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          label="Place of Incorporation"
                          error={
                            formik.touched.trustees
                              ? formik.errors.trustees?.[index] &&
                                formik.errors.trustees.length > 0
                                ? JSON.parse(
                                    JSON.stringify(
                                      formik.errors.trustees[index]
                                    )
                                  ).incorporationPlace
                                : ''
                              : ''
                          }
                          size="lg"
                          className="rounded-2xl border-[1.5px] border-gray-300"
                        />
                      </div>
                      <div className="group relative z-0 mb-6 w-full">
                        <TextAreaFieldWithAutoHeightResize
                          label="Duties and Instructions"
                          isRequired
                          name={`trustees[${index}].dutiesInstruction`}
                          value={trustee.dutiesInstruction}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder=""
                          rows={6}
                          error={
                            formik.touched.trustees
                              ? formik.errors.trustees?.[index] &&
                                formik.errors.trustees.length > 0
                                ? JSON.parse(
                                    JSON.stringify(
                                      formik.errors.trustees[index]
                                    )
                                  ).dutiesInstruction
                                : ''
                              : ''
                          }
                          className="rounded-2xl border-[1.5px] border-gray-300"
                        />
                      </div>
                    </div>
                  </div>
                )}
                {showRemoveButton(
                  index,
                  trustee.trusteeId,
                  data!.length > 1
                ) && (
                  <div className="group relative z-0 mt-1 w-full">
                    {isDeleting ? (
                      <Loader height={20} width={20} type={'Wait'} />
                    ) : (
                      <button
                        type="button"
                        onClick={() => removeTrustee(index, trustee.trusteeId)}
                      >
                        <Icon name={Icons.Delete} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div className="flex items-center justify-between gap-2">
              <div className="flex w-full items-center gap-2">
                <Icon name={Icons.Required} />
                <span className="text-[10px] text-[#7A7C87]">
                  Indicates Required
                </span>
              </div>
              {formik.values.trustees[0].trusteeType && (
                <div className="group z-0 flex w-full items-center justify-end">
                  <div
                    className="flex items-center gap-4"
                    onClick={addMoreTrustee}
                    role="button"
                    tabIndex={-1}
                  >
                    <Icon name={Icons.AddCircleOutline} />
                    <span className="mt-2 text-base font-medium text-[#353535]">
                      Add Another Trustee
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-5 flex w-full items-end justify-between">
              <Button
                variant="outline"
                size="md"
                className="mt-1 w-[9rem] rounded p-[0.1rem] text-primary-900"
                label="Previous"
                type="button"
                onClick={() => onPrevious()}
              ></Button>
              <div className="mt-5 flex items-end justify-end gap-8">
                {parentPath !== 'dashboard' && (
                  <button
                    type="submit"
                    className="font-circular text-[15px] font-bold text-[#353535] underline"
                    onClick={() => {
                      onContinueLater();
                      setIsLater(true);
                    }}
                  >
                    Save and Continue Later
                  </button>
                )}

                <Button
                  variant="primary"
                  size="md"
                  className="mt-5 w-[9rem] rounded p-[0.1rem]"
                  label="Proceed"
                  type="submit"
                  disabled={!formik.isValid}
                  loading={isSubmitting || isLoading}
                ></Button>
              </div>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
